import React, { useState } from 'react';
import { jobService } from '../../services/api';

interface OrderServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    propertyId: string;
    propertyAddress: string;
    tenants: any[];
}

export default function OrderServiceModal({ isOpen, onClose, propertyId, propertyAddress, tenants }: OrderServiceModalProps) {
    const [serviceType, setServiceType] = useState('Gas Safety Certificate');
    const [testDetails, setTestDetails] = useState('');
    const [applianceLocations, setApplianceLocations] = useState('');
    const [requestedDate, setRequestedDate] = useState('');
    const [accessConfirmation, setAccessConfirmation] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const primaryTenant = tenants[0] || {};

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await jobService.orderService({
                propertyId,
                propertyAddress,
                serviceType,
                testDetails,
                applianceLocations,
                requestedDate,
                accessConfirmation,
                tenantName: primaryTenant.name || '',
                tenantPhone: primaryTenant.phone || ''
            });
            alert('Service ordered successfully! Our partner network has been notified.');
            onClose();
        } catch (err: any) {
            console.error(err);
            alert(err.response?.data?.error || 'Failed to order service. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" onClick={onClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden z-10">
                <div className="bg-emerald-600 px-6 py-4 flex justify-between items-center text-white">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <i className="fas fa-tools"></i> Order compliance service
                    </h2>
                    <button onClick={onClose} className="text-emerald-100 hover:text-white transition">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Property Address</label>
                        <input
                            type="text"
                            value={propertyAddress}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Required</label>
                        <select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                            required
                        >
                            <option value="Gas Safety Certificate">Gas Safety Certificate</option>
                            <option value="EICR">EICR (Electrical Safety)</option>
                            <option value="EPC">EPC (Energy Performance)</option>
                            <option value="Portable Appliance Testing (PAT)">Portable Appliance Testing (PAT)</option>
                            <option value="Boiler Service">Boiler Service</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Description / Details</label>
                        <textarea
                            value={testDetails}
                            onChange={(e) => setTestDetails(e.target.value)}
                            rows={2}
                            placeholder="e.g. Annual safety check needed for boiler and gas hob."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                            required
                        ></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Appliance Locations (Optional)</label>
                        <input
                            type="text"
                            value={applianceLocations}
                            onChange={(e) => setApplianceLocations(e.target.value)}
                            placeholder="e.g. Boiler in kitchen cupboard"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Requested Date (Optional)</label>
                            <input
                                type="date"
                                value={requestedDate}
                                onChange={(e) => setRequestedDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Access Instructions</label>
                            <input
                                type="text"
                                value={accessConfirmation}
                                onChange={(e) => setAccessConfirmation(e.target.value)}
                                placeholder="e.g. Keys with tenant"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                                required
                            />
                        </div>
                    </div>
                    {tenants.length > 0 && (
                        <div className="bg-emerald-50 p-3 rounded-md border border-emerald-100 flex items-start gap-3">
                            <i className="fas fa-info-circle text-emerald-600 mt-0.5"></i>
                            <div className="text-sm text-emerald-800">
                                <strong>Tenant Info Attached:</strong> {primaryTenant.name} ({primaryTenant.phone || 'No phone provided'}) will be shared with the engineer for access.
                            </div>
                        </div>
                    )}
                    <div className="pt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-emerald-400 font-bold flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                            ) : (
                                <><i className="fas fa-paper-plane"></i> Submit Order</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
